/**
 * The public auth screens' frame: the Luminaria logo above a card. With no
 * children it shows a loading spinner instead of the card.
 */

import type { ReactNode } from 'react';

// Luminaria Logo - Candle with flame
const LuminariaLogo = () => (
  <svg
    width="64"
    height="64"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="absolute right-full mr-2 text-white max-[480px]:static max-[480px]:mb-2 max-[480px]:mr-0"
    style={{ filter: 'drop-shadow(0 0 20px rgba(255, 255, 255, 0.3))' }}
  >
    {/* Flame */}
    <path d="M12 2C12 2 9 5.5 9 8c0 1.5 1.5 3 3 3s3-1.5 3-3c0-2.5-3-6-3-6z" />
    {/* Candle body */}
    <rect x="8" y="11" width="8" height="11" rx="1" />
    {/* Drip detail */}
    <path d="M10 11v2M14 11v1" />
  </svg>
);

const LoadingSpinner = () => (
  <div className="mt-8 flex flex-col items-center justify-center gap-4">
    <div className="h-12 w-12 animate-spin rounded-full border-4 border-white/10 border-t-teal-600" />
  </div>
);

export function AuthCardLayout({ children }: { children?: ReactNode }) {
  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-bg p-8 text-text max-[480px]:p-4">
      {/* Background decoration */}
      <div
        className="pointer-events-none absolute -bottom-1/2 -left-1/2 -right-1/2 -top-1/2"
        style={{
          background:
            'radial-gradient(circle at 50% 30%, var(--color-primary-tint-15) 0%, var(--color-primary-tint-5) 25%, transparent 50%)',
        }}
      />

      <div className="relative z-10 flex w-full max-w-[420px] flex-col items-center gap-8">
        <div className="relative ml-12 flex flex-row items-center justify-center text-center max-[480px]:ml-0 max-[480px]:flex-col max-[480px]:gap-2">
          <LuminariaLogo />
          <h1 className="m-0 text-4xl font-semibold text-white max-[480px]:text-[2rem]">
            Luminaria
          </h1>
        </div>

        {children ? (
          <div className="w-full rounded-xl border border-border bg-bg-alt p-6 shadow-[0_4px_24px_var(--overlay-black-40)] max-[480px]:p-5">
            {children}
          </div>
        ) : (
          <LoadingSpinner />
        )}
      </div>
    </div>
  );
}
